import React, { useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Icon } from "@sunwing/icons/westjet-vacations";

import {
	Tabs,
	Accordion,
	Heading,
	HeadingSize,
	Row,
	Cell,
	Link,
	StickyNav,
	RoomCard,
	Button,
	Modal,
	ModalSizes,
	InfoCard,
} from "@sunwing/wvi-components";
import { CardsContainerGrid } from "../../CardsContainer";
import { RCL as useTranslation } from "../../RCL";
import { ImageConnector } from "../../../connectors/ImageConnector";
import * as styles from "./PageTabs.module.scss";

const Description = ({ card }) => {
	const [showFacilities, setShowFacilities] = useState(false);
	const [showExtraFeatures, setShowExtraFeatures] = useState(false);
	const [showMap, setShowMap] = useState(false);

	const translations = {
		location: useTranslation({ searchKey: "location" }),
		sqFtApprox: useTranslation({ searchKey: "sq-ft-approx" }),
		cuisine: useTranslation({ searchKey: "cuisine" }),
		style: useTranslation({ searchKey: "style" }),
		daily: useTranslation({ searchKey: "daily" }),
		notes: useTranslation({ searchKey: "notes" }),
		contactInformation: useTranslation({ searchKey: "contact-information" }),
		viewHotelMap: useTranslation({ searchKey: "view-hotel-map" }),
		airportName: useTranslation({ searchKey: "airport-name" }),
		transferTime: useTranslation({ searchKey: "airport-transfer-time-vwq" }),
		checkInTime: useTranslation({ searchKey: "check-in-time" }),
		checkOutTime: useTranslation({ searchKey: "check-out-time" }),
		seeFullDetails: useTranslation({ searchKey: "see-full-details" }),
		airportInfoDisclaimer: useTranslation({ searchKey: "airport-transfer-disclaimer" }),
		airportTransferDisclaimer: useTranslation({ searchKey: "airporttransferdisclaimer" }),
		allInclusive: useTranslation({ searchKey: "all-inclusive" }),
		amenities: useTranslation({ searchKey: "amenities" }),
		moreInfo: useTranslation({ searchKey: "more-info" }),
		roomDetails: useTranslation({ searchKey: "room-details" }),
		maxOccupancy: useTranslation({ searchKey: "max-occupancy-wvi" }),
		maxAdults: useTranslation({ searchKey: "max-adults" }),
		maxChildren: useTranslation({ searchKey: "max-children" }),
		loadAll: useTranslation({ searchKey: "load-all" }),
		loadMore: useTranslation({ searchKey: "load-more" }),
	};

	function isValidObject(obj) {
		return Object.keys(obj)?.length > 0;
	}

	return (
		<>
			<div>{card?.resortMap}</div>
			{card?.description &&
				(React.isValidElement(card?.description) ? (
					card.description
				) : (
					<div
						className={
							card.images ? styles?.description : cx(styles?.description, styles?.descriptionFull)
						}
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{ __html: card?.description }}
					/>
				))}

			{card.alerts && (
				<ul>
					{card.alerts.map(alert => (
						// eslint-disable-next-line react/jsx-key
						<li className={styles?.alerts}>
							{/* eslint-disable-next-line react/no-danger */}
							<div dangerouslySetInnerHTML={{ __html: alert }} />
						</li>
					))}
				</ul>
			)}

			{card.accordions && (
				<div
					className={cx(
						styles?.description,
						!card.images && styles?.descriptionFull,
						styles.accordionWrapper
					)}>
					{card.accordions.map(accordion => (
						// eslint-disable-next-line react/jsx-key
						<div className={styles?.accordion}>
							{accordion.title && (
								<Heading Tag="span" size={HeadingSize.eyebrow}>
									{accordion.title}
								</Heading>
							)}
							<Accordion collapsible>
								{accordion.panels?.map(panel => (
									<Accordion.Item borderlessTop key={panel.key} value={panel.key}>
										<Accordion.Trigger>
											<Accordion.Label>{panel.title}</Accordion.Label>
										</Accordion.Trigger>
										<Accordion.Content>
											<>
												{panel?.cost && (
													<>
														<div
															// eslint-disable-next-line react/no-danger
															dangerouslySetInnerHTML={{
																__html: panel.cost,
															}}
														/>
														<br />
													</>
												)}
												<div
													// eslint-disable-next-line react/no-danger
													dangerouslySetInnerHTML={{
														__html: panel.content,
													}}
												/>
												{panel.contact ? (
													<>
														<br />
														<div>
															<strong>
																{translations.contactInformation
																	? `${translations.contactInformation}: `
																	: "Contact Information: "}
															</strong>
															<br />
															<span
																// eslint-disable-next-line react/no-danger
																dangerouslySetInnerHTML={{
																	__html: panel.contact,
																}}
															/>
														</div>
													</>
												) : null}
											</>
										</Accordion.Content>
									</Accordion.Item>
								))}
							</Accordion>
						</div>
					))}
				</div>
			)}

			{card.collections && (
				<div className={cx(styles?.description, !card.images && styles?.descriptionFull)}>
					<Row cols={3}>
						{card.collections.map(collection => (
							<Cell key={collection.key}>
								<InfoCard
									classNames={{
										cardImage: styles?.infoCardImage,
									}}
									content={collection.collectionDescription}
									header={collection.collectionName}
									image={{
										alt: collection.collectionName,
										source: {
											src: collection.collectionImage,
										},
									}}
									variant="horizontal"
								/>
							</Cell>
						))}
					</Row>
				</div>
			)}

			{card.features && (
				<div className={cx(styles?.description, !card.images && styles?.descriptionFull)}>
					<Row cols={2}>
						{card.features.map(feature => (
							<Cell key={feature.key}>
								<Heading Tab="h4" size={HeadingSize.sm} className={styles?.featuresTitle}>
									{feature.title}
								</Heading>
								<ul className={styles?.featuresList}>
									{feature.panels.map(item => (
										<li key={item.key}>{item?.title}</li>
									))}
								</ul>
								<Button variant="actionOutline" onClick={() => setShowExtraFeatures(feature)}>
									{translations.seeFullDetails}
								</Button>
							</Cell>
						))}
					</Row>
					<Modal
						isOpen={showExtraFeatures}
						onClose={() => setShowExtraFeatures(false)}
						size={ModalSizes.lg}
						title={showExtraFeatures.title}
						classNames={{
							content: styles?.modalContent,
						}}>
						<Accordion collapsible type="single">
							{showExtraFeatures.panels?.map(feature => (
								<Accordion.Item key={feature.key} value={feature.key} borderlessTop>
									<Accordion.Trigger>
										<Accordion.Label>{feature.title}</Accordion.Label>
									</Accordion.Trigger>
									<Accordion.Content>
										{feature.content && (
											<div
												// eslint-disable-next-line react/no-danger
												dangerouslySetInnerHTML={{
													__html: feature.content,
												}}
											/>
										)}
									</Accordion.Content>
								</Accordion.Item>
							))}
						</Accordion>
					</Modal>
				</div>
			)}

			{card.facilities && (
				<div className={cx(styles?.description, !card.images && styles?.descriptionFull)}>
					<div className={styles?.facilitiesSection}>
						<ul className={styles?.facilitiesList}>
							{card.facilities.map(facility => (
								<li key={facility.title}>{facility.title}</li>
							))}
						</ul>
						<Button variant="actionOutline" onClick={() => setShowFacilities(true)}>
							{translations.seeFullDetails}
						</Button>
					</div>
					<Modal
						classNames={{
							title: styles?.modalTitle,
							content: styles?.modalContent,
						}}
						title={card.cardText}
						isOpen={showFacilities}
						onClose={() => setShowFacilities(false)}
						size={ModalSizes.lg}>
						<Accordion collapsible type="single">
							{card.facilities.map(facility => (
								<Accordion.Item key={facility.key} value={facility.key} borderlessTop>
									<Accordion.Trigger>
										<Accordion.Label>{facility.title}</Accordion.Label>
									</Accordion.Trigger>
									<Accordion.Content>
										{facility.features && (
											<ul className={styles?.description}>
												{facility.features.map((feature, index) => (
													// eslint-disable-next-line react/no-array-index-key
													<li key={index}>{feature}</li>
												))}
											</ul>
										)}
									</Accordion.Content>
								</Accordion.Item>
							))}
						</Accordion>
					</Modal>
				</div>
			)}

			{card.dining && (
				<div className={cx(styles?.description, !card.images && styles?.descriptionFull)}>
					<Tabs defaultValue={card.dining[0].category}>
						<Tabs.List asNav>
							{card.dining.map((section, index) => (
								// eslint-disable-next-line react/no-array-index-key
								<Tabs.Trigger key={`dining-${index}`} value={section.category}>
									<Heading className={styles?.diningCategoryTitle} Tag="h4" size={HeadingSize.sm}>
										{section.category}{" "}
									</Heading>
								</Tabs.Trigger>
							))}
						</Tabs.List>
						{card.dining.map((section, index) => (
							<Tabs.Content
								// eslint-disable-next-line react/no-array-index-key
								key={`dining-content-${index}`}
								value={section.category}
								disableAutoScroll={true}>
								<div className={styles?.diningContainer}>
									{section.locations.map(location => (
										<div key={location.title}>
											<Heading
												className={styles?.diningTitle}
												Tag={HeadingSize.h5}
												size={HeadingSize.xs}>
												{location.title}
											</Heading>
											<div>
												<ul>
													{location.style && (
														<li>
															{translations.style}: {location.style}
														</li>
													)}
													{location.types && <li className={styles?.detail}>{location.types}</li>}
													{location.location && (
														<li className={styles?.detail}>
															{translations.location}: {location.location}
														</li>
													)}
													{location.cuisine && (
														<li className={styles?.detail}>
															{translations.cuisine}: {location.cuisine}
														</li>
													)}
													{!!location.operationHours?.length &&
														location?.operationHours.map(hours => {
															const splitArr = hours.split(" ");
															splitArr.forEach(item => {
																if (item === "Daily") {
																	splitArr[splitArr.indexOf("Daily")] = translations.daily;
																}
															});
															return <li key={hours}>{splitArr.join(" ")}</li>;
														})}
												</ul>

												{(location.additionalComments || location.amenities) && (
													<div className={styles?.rightSection}>
														<span className={cx(styles?.detail, styles?.notes)}>
															{translations.notes}:
															<ul>
																{!!location.additionalComments && (
																	<li
																		// eslint-disable-next-line react/no-danger
																		dangerouslySetInnerHTML={{
																			__html: location.additionalComments,
																		}}
																	/>
																)}
																{!!location.amenities?.length &&
																	location.amenities.map((amenity, index) => (
																		// eslint-disable-next-line react/no-array-index-key
																		<li key={index}>{amenity}</li>
																	))}
															</ul>
														</span>
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							</Tabs.Content>
						))}
					</Tabs>
				</div>
			)}

			{card.categoriesSection && (
				<div className={cx(styles?.description, !card.images && styles?.descriptionFull)}>
					<div className={styles?.categoriesSection}>
						{card.categoriesSection.title && (
							<h3 className={styles?.categoriesSectionTitle}>{card.categoriesSection.title}</h3>
						)}
						{!!card.categoriesSection.categories?.length && (
							<ul className={styles?.categories}>
								{card.categoriesSection.categories.map(category => {
									const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
									return <li key={category}>{capitalizedCategory}</li>;
								})}
							</ul>
						)}
					</div>
				</div>
			)}

			{card.rooms && (
				<div className={cx(styles?.description, !card.images && styles?.descriptionFull)}>
					<CardsContainerGrid
						labels={{
							loadAll: translations.loadAll,
							loadMore: translations.loadMore,
						}}>
						{card.rooms.map(room => (
							<RoomCard
								key={room.id}
								header={room.title}
								images={room.images ?? []}
								labels={{
									extraDetailsTitle: translations.amenities,
									primaryDetailsTitle: translations.roomDetails,
								}}
								link={{
									children: translations.moreInfo,
									href: "#",
								}}
								roomDetails={{
									more: room?.roomAmenities,
									primary: [
										room.roomSize && {
											icon: "square-footage",
											label: `${room.roomSize} ${translations.sqFtApprox}`,
										},
										room.maxOccupancy && {
											icon: "maximum-occupancy",
											label: `${translations.maxOccupancy} ${room.maxOccupancy}`,
										},
										room.maxNumAdults && {
											icon: "adults",
											label: `${translations.maxAdults} ${room.maxNumAdults}`,
										},
										room.maximumChildren && {
											icon: "children",
											label: `${translations.maxChildren} ${room.maximumChildren}`,
										},
									].filter(Boolean),
								}}
							/>
						))}
					</CardsContainerGrid>
				</div>
			)}

			{card.locationDetails && (
				<div className={cx(styles?.description, !card.images && styles?.descriptionFull)}>
					<Row cols={2}>
						{isValidObject(card.locationDetails?.hotelLocation) && (
							<Cell>
								<div className={styles?.locationDetail}>
									<Icon name={card.locationDetails?.hotelLocation?.icon} size="3xl" />
									<Heading className={styles?.locationLabel} as="h4" size={HeadingSize.sm}>
										{card.locationDetails?.hotelLocation?.label}
									</Heading>
								</div>
								<p className={styles?.locationDescription}>
									{card.locationDetails?.hotelLocation?.address}
								</p>
								{card.locationDetails?.hotelLocation?.resortMap && (
									<Link
										className={styles?.locationLink}
										icon="arrow-right-micro"
										size="sm"
										onClick={() => setShowMap(true)}>
										{translations.viewHotelMap}
									</Link>
								)}
							</Cell>
						)}
						{isValidObject(card.locationDetails?.airportInfo) && (
							<Cell>
								<div className={styles?.locationDetail}>
									<Icon name={card.locationDetails?.airportInfo?.icon} size="3xl" />
									<Heading className={styles?.locationLabel} as="h4" size={HeadingSize.sm}>
										{card.locationDetails?.airportInfo?.label}
									</Heading>
								</div>
								<div className={styles?.airportInfo}>
									<div>
										<span className={styles?.airportName}>{translations.airportName}</span>:{" "}
										<span>{card.locationDetails?.airportInfo?.name}</span>
									</div>
									<div>
										<span className={styles?.airportName}>{translations.transferTime}</span>:{" "}
										<span>{card.locationDetails?.airportInfo?.transferTime}</span>
									</div>
								</div>
								<div className={styles?.disclaimer}>{translations.airportInfoDisclaimer}</div>
							</Cell>
						)}
						{isValidObject(card.locationDetails?.hotelInfo) && (
							<Cell>
								<div className={styles?.locationDetail}>
									<Icon name={card.locationDetails?.hotelInfo?.icon} size="3xl" />
									<Heading className={styles?.locationLabel} as="h4" size={HeadingSize.sm}>
										{card.locationDetails?.hotelInfo?.label}
									</Heading>
								</div>
								<div className={styles?.airportInfo}>
									<div>
										<span className={styles?.airportName}>{translations.checkInTime}</span>:{" "}
										<span>{card.locationDetails?.hotelInfo?.checkInTime}</span>
									</div>
									<div>
										<span className={styles?.airportName}>{translations.checkOutTime}</span>:{" "}
										<span>{card.locationDetails?.hotelInfo?.checkOutTime}</span>
									</div>
								</div>
								<div className={styles?.disclaimer}>{translations.airportTransferDisclaimer}</div>
							</Cell>
						)}
					</Row>
					<Modal
						className={styles?.resoprtMapModal}
						isOpen={showMap}
						onClose={() => setShowMap(false)}>
						<img src={card?.locationDetails?.hotelLocation?.resortMap} loading="lazy" />
					</Modal>
				</div>
			)}

			{card.images && (
				<div className={styles?.descriptionImages}>
					{card.images.map(
						// eslint-disable-next-line react/jsx-key
						image => image.src && <ImageConnector alt={image.alt} src={image.src} />
					)}
				</div>
			)}
		</>
	);
};

const PageTabs = ({ cards }) => {
	return (
		<div id="description-tabs">
			<StickyNav sections={cards}>
				{cards.map(card => (
					<StickyNav.Item key={card.cardKey} value={card.cardKey}>
						<div>{card.cardText}</div>
					</StickyNav.Item>
				))}
			</StickyNav>
			<div>
				{cards.map((card, index) => (
					<>
						<div id={card.id} key={card.cardKey} className={styles?.detailsSection}>
							<div className={styles?.detailsSectionHeader}>
								{card.cardText && (
									<Heading Tag="h3" size={HeadingSize.md} className={styles?.title}>
										{card.cardText}
									</Heading>
								)}
							</div>
							<Description card={card} />
						</div>
						{/* Render hr only if it's not the last card */}
						{index !== cards.length - 1 && <hr className={styles?.divider} />}
					</>
				))}
			</div>
		</div>
	);
};

Description.propTypes = {
	card: PropTypes.object,
	hotelCountry: PropTypes.string,
};
Description.defaultProps = {
	card: null,
	hotelCountry: undefined,
};

PageTabs.propTypes = {
	activeKey: PropTypes.string,
	cards: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			order: PropTypes.number,
			cardKey: PropTypes.string,
			icon: PropTypes.string,
			cardText: PropTypes.string,
			description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
			images: PropTypes.arrayOf(
				PropTypes.shape({
					src: PropTypes.string,
					alt: PropTypes.string,
				})
			),
			alerts: PropTypes.arrayOf(
				PropTypes.shape({
					body: PropTypes.string,
				})
			),
			accordions: PropTypes.arrayOf(
				PropTypes.shape({
					title: PropTypes.string,
					panels: PropTypes.arrayOf(
						PropTypes.shape({
							key: PropTypes.string,
							title: PropTypes.string,
							content: PropTypes.string,
							cost: PropTypes.string,
							contact: PropTypes.string,
						})
					),
				})
			),
			facilities: PropTypes.arrayOf(
				PropTypes.shape({
					title: PropTypes.string,
					features: PropTypes.arrayOf(PropTypes.string),
				})
			),
			dining: PropTypes.arrayOf(
				PropTypes.shape({
					category: PropTypes.string,
					location: PropTypes.arrayOf(
						PropTypes.shape({
							title: PropTypes.string,
							style: PropTypes.string,
							location: PropTypes.string,
							dietaryRestrictions: PropTypes.string,
							cuisine: PropTypes.string,
							features: PropTypes.shape({
								bar: PropTypes.string,
								restaurant: PropTypes.string,
							}),
							services: PropTypes.shape({
								bar: PropTypes.string,
								restaurant: PropTypes.string,
							}),
							types: PropTypes.string,
							minimumAge: PropTypes.string,
							dressCode: PropTypes.shape({
								bar: PropTypes.string,
								restaurant: PropTypes.string,
							}),
							ageRestriction: PropTypes.string,
							operationHours: PropTypes.arrayOf(PropTypes.string),
							notes: PropTypes.string,
						})
					),
				})
			),
			categoriesSection: PropTypes.shape({
				title: PropTypes.string,
				categories: PropTypes.arrayOf(PropTypes.string),
			}),
			rooms: PropTypes.arrayOf(
				PropTypes.shape({
					title: PropTypes.string,
					images: PropTypes.arrayOf(
						PropTypes.shape({
							src: PropTypes.string,
						})
					),
					maxOccupancy: PropTypes.string,
					maxNumAdults: PropTypes.string,
					maximumChildren: PropTypes.string,
					roomSize: PropTypes.string,
					roomFeatures: PropTypes.arrayOf(PropTypes.string),
					beds: PropTypes.string,
					beddingType: PropTypes.arrayOf(PropTypes.string),
					connectivityType: PropTypes.arrayOf(PropTypes.string),
					bonusFeatures: PropTypes.arrayOf(PropTypes.string),
					roomServices: PropTypes.arrayOf(PropTypes.string),
					accessibilityFeatures: PropTypes.arrayOf(PropTypes.string),
					entertainment: PropTypes.arrayOf(PropTypes.string),
					outdoorFeatures: PropTypes.arrayOf(PropTypes.string),
					miscellaneousFeatures: PropTypes.arrayOf(PropTypes.string),
					roomViews: PropTypes.arrayOf(PropTypes.string),
					roomLocations: PropTypes.arrayOf(PropTypes.string),
					roomAmenities: PropTypes.arrayOf(PropTypes.string),
					roomDescription: PropTypes.string,
				})
			),
			attributes: PropTypes.arrayOf(
				PropTypes.shape({
					label: PropTypes.string,
					value: PropTypes.string,
				})
			),
			miceType: PropTypes.string,
		})
	),
};
PageTabs.defaultProps = {
	activeKey: undefined,
	variant: "classic",
	cards: [],
	hotelCountry: undefined,
};

export default PageTabs;
export { PageTabs };
